import React, { useState, useMemo, useEffect, useContext } from 'react';
import { Tabs, Tab } from 'react-bootstrap';
import * as ROLES from '../../../constants/roles';
import './styles.css';
import Profile from './Profile';
import Password from '../Password';
import Stores from './Stores';
import Billing from './Billing';
import PartnerCarriers from './PartnerCarriers';
import Teammates from '../Teammates';
import TariffTable from '../TariffTable';
import { Rating } from '@material-ui/lab';
import api from '@/utilities/api';
import { UserContext } from '@/components/App';
import { LIST_TARIFFS_BY_CLIENT_ID, UPSERT_DRIVER_TARIFFS, UPSERT_PRICING_OVERRIDES } from '../Tariffs/graphql';
import { AGGREGATE_RATING_BY_REVIEWEE } from '@/graphql/queries/ratings';
import { useQuery, useLazyQuery, useMutation } from '@apollo/client';
import * as Sentry from '@sentry/react';

import { Tooltip } from '@material-ui/core';
import ShippingPartners from '../ShippingPartners';
import { TRUCKS_TAB, useTrucksTab } from '../hooks/useTrucksTab';
import { useClientUser } from '@/hooks';
import { BROKER } from '@/constants/featureFlags';
import CustomDocuments from '../CustomDocuments';
import { TabCard } from '../blocks';
import { css } from '@emotion/react';
import ArchiveDocuments from '../ArchiveDocuments';
import SLARequirements from '../SLARequirements';
import MilestoneThresholds from '../MilestoneThresholds';
import EmailSubscriptions from '../EmailSubscriptions';
import CustomTags from '../CustomTags';
import Surveys from '../Surveys';
import SKUsAndServices from '../SKUsAndServices';

function ShipperAccountPage(props) {
    const { user, client: currentShipper } = useContext(UserContext);
    const { client_id, writableTariffClientIds } = useClientUser();
    const onSelectTab = useTrucksTab();

    const [fetchTariffs, { data }] = useLazyQuery(LIST_TARIFFS_BY_CLIENT_ID, {
        variables: {
            client_ids: writableTariffClientIds,
        },
    });

    const [fetchOverallRating, { data: ratings }] = useLazyQuery(AGGREGATE_RATING_BY_REVIEWEE);

    const [upsertDriverTariff] = useMutation(UPSERT_DRIVER_TARIFFS, {
        update: (cache, { data: { updated } }) => {
            cache.updateQuery(
                {
                    query: LIST_TARIFFS_BY_CLIENT_ID,
                    variables: {
                        client_ids: writableTariffClientIds,
                    },
                },
                (data) => {
                    let { driver_tariffs, ...rest } = data;
                    let clone = [...driver_tariffs];
                    const update = updated.returning[0];

                    const idx = clone.findIndex((m) => m.mapping_id === update.mapping_id);
                    if (idx >= 0) {
                        clone[idx] = update;
                    } else {
                        clone = [...clone, update];
                    }

                    return {
                        ...rest,
                        driver_tariffs: clone,
                    };
                }
            );
        },
    });

    const [upsertPo] = useMutation(UPSERT_PRICING_OVERRIDES, {
        update: (cache, { data: { updated } }) => {
            cache.updateQuery(
                {
                    query: LIST_TARIFFS_BY_CLIENT_ID,
                    variables: {
                        client_ids: writableTariffClientIds,
                    },
                },
                (data) => {
                    let { overrides, ...rest } = data;
                    let clone = [...overrides];
                    const [update] = updated;

                    const idx = clone.findIndex((po) => po.pricing_override_id === update.pricing_override_id);
                    if (idx >= 0) {
                        clone[idx] = update;
                    } else {
                        clone = [...clone, update];
                    }

                    return {
                        ...rest,
                        overrides: clone,
                    };
                }
            );
        },
        onError: (e) => {
            Sentry.captureException(e);
        },
    });

    useEffect(() => {
        if (client_id) {
            fetchOverallRating({ variables: { reviewee_id: client_id } });
            fetchTariffs();
        }
    }, [client_id]);

    const [tariffs, overrides, driverMappings] = useMemo(() => {
        if (data) {
            const { tariffs, overrides, driver_tariffs } = data;
            return [tariffs, overrides, driver_tariffs];
        }

        return [[], [], []];
    }, [data]);

    const rating = useMemo(() => {
        if (ratings?.results?.aggregate?.avg?.rating) {
            return ratings?.results?.aggregate?.avg?.rating;
        }

        return 0;
    }, [ratings]);

    return (
        <div style={{ margin: '0 80px', paddingBottom: '30px' }}>
            <div className="d-flex justify-content-between my-3">
                <h2 className="  accountwidth" style={{ fontWeight: '700' }}>
                    My Account
                </h2>
                {rating >= 3 && (
                    <div className="align-self-center d-flex">
                        <h5 className="text-nowrap">My Rating: </h5>
                        <Tooltip
                            title={rating.toFixed(1) + ' / 5'}
                            arrow
                            PopperProps={{ style: { marginTop: '-10px' } }}
                        >
                            <div>
                                <Rating readOnly value={rating} precision={0.1} />
                            </div>
                        </Tooltip>
                    </div>
                )}
            </div>
            <Tabs defaultActiveKey="Profile" mountOnEnter={true} unmountOnExit={true} onSelect={onSelectTab}>
                <Tab tabClassName="tabLink" eventKey="Profile" title="Profile">
                    <Profile
                        currentShipperRef={client_id}
                        currentShipper={currentShipper}
                        device_settings={user?.device_settings}
                    />
                </Tab>
                <Tab tabClassName="tabLink" eventKey="Stores" title="Stores">
                    <Stores currentClientRef={client_id} currentClient={currentShipper} />
                </Tab>
                <Tab tabClassName="tabLink" eventKey={TRUCKS_TAB} title="Trucks" />
                <Tab tabClassName="tabLink" eventKey="SKUs" title="SKU's / Services">
                    <SKUsAndServices clientRef={client_id} />
                </Tab>
                <Tab tabClassName="tabLink" eventKey="Custom Documents" title="Custom Documents">
                    <CustomDocuments client={currentShipper} />
                    <ArchiveDocuments client_id={client_id} isAdmin={false} editable={true} />
                    <TabCard
                        css={css`
                            padding: 2rem;
                        `}
                    >
                        <ArchiveDocuments client_id={client_id} isAdmin={true} editable={false} />
                    </TabCard>
                </Tab>
                <Tab tabClassName="tabLink" eventKey="SLA Requirements" title="SLA Requirements">
                    <SLARequirements />
                </Tab>
                <Tab tabClassName="tabLink" eventKey="Milestone Thresholds" title="Milestone Thresholds">
                    <MilestoneThresholds client={currentShipper} />
                </Tab>
                <Tab tabClassName="tabLink" eventKey="Email Subscriptions" title="Email Subscriptions">
                    <EmailSubscriptions client={currentShipper} />
                </Tab>

                <Tab tabClassName="tabLink" eventKey="Billing" title="Billing">
                    <Billing currentShipper={currentShipper} />
                </Tab>
                <Tab tabClassName="tabLink" eventKey="Password" title="Password">
                    <Password />
                </Tab>
                <Tab tabClassName="tabLink" eventKey="Teammates" title="Teammates">
                    <Teammates
                        teamLeaderType="shipper"
                        userId={client_id}
                        tariffs={tariffs}
                        driverMappings={driverMappings}
                        callbacks={{ upsertDriverTariff }}
                    />
                </Tab>
                <Tab tabClassName="tabLink" eventKey="Tariffs" title="Tariffs">
                    <TariffTable
                        client={currentShipper}
                        tariffs={tariffs}
                        overrides={overrides}
                        driverMappings={driverMappings}
                        callbacks={{ upsertPo, upsertDriverTariff }}
                    />
                </Tab>
                <Tab tabClassName="tabLink" eventKey="Custom Tags" title="Tags">
                    <CustomTags />
                </Tab>
                <Tab tabClassName="tabLink" eventKey="Surveys" title="Surveys">
                    <Surveys />
                </Tab>
                <Tab tabClassName="tabLink" eventKey="Partner Carriers" title="Partner Carriers">
                    <PartnerCarriers />
                </Tab>
            </Tabs>
        </div>
    );
}

export default ShipperAccountPage;
